import createAPIServices from './CreateApiServices'
import {getLocalData} from './StorageServices'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import CryptoJS from 'crypto-js'

const BASE_URL = process.env.REACT_APP_NEXT_MOCKUP_GENERATOR_SERVICE

const api = createAPIServices({
    baseUrl: BASE_URL,
    timeout: 180000,
})

let configCache = null
let fingerprintCache = null

const FINGERPRINT_STORAGE_KEY = 'device_fingerprint'

// Initialize fingerprint cache from local storage on module load
try {
    const storedFingerprint = localStorage.getItem(FINGERPRINT_STORAGE_KEY)
    if (storedFingerprint) {
        fingerprintCache = storedFingerprint
    }
} catch (error) {
    // Ignore errors during initialization
}

/**
 * Get fingerprint from local storage
 * @returns {string|null} - Fingerprint from local storage or null
 */
const getFingerprintFromLocalStorage = () => {
    try {
        return localStorage.getItem(FINGERPRINT_STORAGE_KEY)
    } catch (error) {
        console.error('[GenerateBackground] Failed to get fingerprint from local storage:', error)
        return null
    }
}

/**
 * Save fingerprint to local storage
 * @param {string} fingerprint - The fingerprint to save
 */
const saveFingerprintToLocalStorage = (fingerprint) => {
    try {
        localStorage.setItem(FINGERPRINT_STORAGE_KEY, fingerprint)
    } catch (error) {
        console.error('[GenerateBackground] Failed to save fingerprint to local storage:', error)
    }
}

/**
 * Generate a random string for device ID
 * @returns {string} - Random device ID
 */
const generateRandomDeviceId = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
}

/**
 * Set a random device ID (overrides fingerprint cache)
 * @returns {string} - The new random device ID
 */
export const setRandomDeviceId = () => {
    fingerprintCache = generateRandomDeviceId()
    saveFingerprintToLocalStorage(fingerprintCache)
    return fingerprintCache
}

/**
 * Get device fingerprint using FingerprintJS
 * @returns {Promise<string>} - The visitor ID (fingerprint)
 */
const getDeviceFingerprint = async () => {
    // Check cache first
    if (fingerprintCache) {
        return fingerprintCache
    }

    // Check local storage
    const storedFingerprint = getFingerprintFromLocalStorage()
    if (storedFingerprint) {
        fingerprintCache = storedFingerprint
        return fingerprintCache
    }

    // Generate new fingerprint if not found in cache or local storage
    try {
        const fp = await FingerprintJS.load({
            monitors: [],
            debug: false,
            enrollment: {
                enabled: false,
            },
            components: {
                canvas: false,
                webgl: false,
                audio: false,
                screenFrame: false,
            },
        })
        const result = await fp.get()
        fingerprintCache = result.visitorId
        // Save to local storage
        saveFingerprintToLocalStorage(fingerprintCache)
        return fingerprintCache
    } catch (error) {
        console.error('[GenerateBackground] Failed to get device fingerprint:', error)
        // Return a fallback identifier if fingerprint fails
        const fallbackFingerprint = 'unknown-device'
        fingerprintCache = fallbackFingerprint
        saveFingerprintToLocalStorage(fallbackFingerprint)
        return fallbackFingerprint
    }
}

/**
 * Encrypt identifiedId using AES-CTR with a secret from config
 * Uses CTR mode to avoid padding issues (no padding required)
 * @param {string} text - The current identifiedId (device fingerprint)
 * @param {string} secret - Secret key from config
 * @returns {string} - Encrypted identifiedId in "iv:encrypted" base64 format
 */
export function encrypt(text, secret) {
    if (!text || !secret) {
        return text
    }

    // Derive key from secret using SHA-256
    const key = CryptoJS.SHA256(secret)

    // Generate random IV (16 bytes for AES)
    const iv = CryptoJS.lib.WordArray.random(16)

    // Encrypt using AES-CBC mode (PKCS7 padding)
    const encrypted = CryptoJS.AES.encrypt(text, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
    })

    const ivBase64 = CryptoJS.enc.Base64.stringify(iv)
    const cipherBase64 = CryptoJS.enc.Base64.stringify(encrypted.ciphertext)

    // Return "iv:encrypted" in base64
    return `${ivBase64}:${cipherBase64}`
}

/**
 * Decode JWT token to get payload
 * @param {string} token - JWT token
 * @returns {Object|null} - Decoded payload or null if invalid
 */
const decodeJWT = (token) => {
    if (!token || typeof token !== 'string') {
        return null
    }

    try {
        // JWT format: header.payload.signature
        const parts = token.split('.')
        if (parts.length !== 3) {
            return null
        }

        // Decode payload (second part)
        // Base64URL decode: replace - with +, _ with /, and add padding if needed
        const base64Url = parts[1]
        let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')

        // Add padding if needed
        while (base64.length % 4) {
            base64 += '='
        }

        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        )

        return JSON.parse(jsonPayload)
    } catch (error) {
        console.error('[GenerateBackground] Failed to decode JWT token:', error)
        return null
    }
}

/**
 * Get email from access token
 * @returns {string|null} - Email from token or null
 */
const getEmailFromToken = () => {
    const accessToken = getLocalData('accessToken')
    if (!accessToken) {
        return null
    }

    const decoded = decodeJWT(accessToken)
    if (!decoded) {
        return null
    }

    // Try common email fields in JWT payload
    return decoded.email || null
}

const calculateGCD = (a, b) => {
    return b === 0 ? a : calculateGCD(b, a % b)
}

const calculateSize = (maxDimension) => {
    // Dynamic size calculation based on max dimension
    // Supports: 1K, 2K, 4K, 8K
    const sizeInK = maxDimension / 1000
    if (sizeInK >= 8) {
        return '8k'
    } else if (sizeInK >= 4) {
        return '4k'
    } else if (sizeInK >= 2) {
        return '2k'
    } else {
        // For dimensions less than 2000, use 1K
        return '1k'
    }
}

/**
 * Convert resolution to ratio string (e.g., "1:1", "16:9", "4:3")
 * Calculates ratio using GCD to get the simplest form
 * @param {number} width
 * @param {number} height
 * @returns {string}
 */
const calculateRatio = (width, height) => {
    const divisor = calculateGCD(width, height)
    const ratioWidth = width / divisor
    const ratioHeight = height / divisor

    // Return the simplified ratio
    return `${ratioWidth}:${ratioHeight}`
}

export const convertResolutionToSizeAndRatio = (resolutionValue, resolutionLabel) => {
    // Parse dimensions from label (e.g., "1920 x 1080" or "1000 x 1000")
    // Supports various formats: "1920 x 1080", "1920x1080", "1920 X 1080", etc.
    const match = resolutionLabel.match(/(\d+)\s*[xX×]\s*(\d+)/i)

    if (!match) {
        // Fallback: use value as square dimension
        const dimension = parseInt(resolutionValue) || 1000
        return {
            size: calculateSize(dimension),
            ratio: '1:1',
        }
    }

    const width = parseInt(match[1])
    const height = parseInt(match[2])
    const maxDimension = Math.max(width, height)

    // Calculate size and ratio dynamically
    const size = calculateSize(maxDimension)
    const ratio = calculateRatio(width, height)

    return {size, ratio}
}

export const loadGenerateBackgroundConfig = async () => {
    if (configCache) {
        return configCache
    }

    try {
        const response = await api.makePublicRequest({
            url: '/genai/config',
            method: 'GET',
        })

        // Response structure: { data: { success: true, data: {...} } }
        const configData = response?.data?.data || response?.data || response

        // Normalize config structure to match expected format
        const config = {
            dailyFreeCredit: configData.freeToken || 2,
            creditPerImage: configData.creditPerImage || 1,
            creditPerImageResolution: configData.creditPerImageResolution || 1,
            secret: configData.secret || null,
            refreshPerDays: configData.refreshPerDays || 30,
            resolution: configData.resolutions || [
                {value: '1000', label: '1000 x 1000'},
                {value: '1200', label: '1200 x 1200'},
                {value: '1920', label: '1920 x 1080'},
                {value: '2000', label: '2000 x 2000'},
            ],
            maxFileSize: configData.maxFileSize || 7 * 1024 * 1024,
            maxPromptLength: configData.maxPromptLength || 500,
            minPromptLength: configData.minPromptLength || 20,
        }

        configCache = config
        return config
    } catch (error) {
        console.error('[GenerateBackgroundConfig] Failed to load config from API:', error)
        // Return default config on error
        const defaultConfig = {
            dailyFreeCredit: 2,
            creditPerImage: 1,
            secret: null,
            creditPerImageResolution: 1,
            resolution: [
                {value: '1000', label: '1000 x 1000'},
                {value: '1200', label: '1200 x 1200'},
                {value: '1920', label: '1920 x 1080'},
                {value: '2000', label: '2000 x 2000'},
            ],
            maxFileSize: 7 * 1024 * 1024,
            maxPromptLength: 500,
            minPromptLength: 20,
        }
        // Cache default config to avoid repeated API calls on error
        configCache = defaultConfig
        return defaultConfig
    }
}

/**
 * Build headers for genai API requests
 * Includes x-identified-id, x-store-slug, and x-owner-email headers
 * @returns {Promise<Object>} - Headers object
 */
export const buildGenaiHeaders = async () => {
    // Load config to get secret for encrypting identifiedId
    const [deviceFingerprint, storeSlug, ownerEmail, config] = await Promise.all([
        getDeviceFingerprint(),
        Promise.resolve(getLocalData('storeId') || null),
        Promise.resolve(getEmailFromToken() || null),
        loadGenerateBackgroundConfig(),
    ])

    let identifiedId = deviceFingerprint.trim()

    // Encrypt identifiedId if secret is available
    if (deviceFingerprint && config?.secret) {
        try {
            identifiedId = encrypt(deviceFingerprint.trim(), config.secret)
        } catch (error) {
            console.error('[GenerateBackground] Failed to encrypt identifiedId:', error)
            // Fallback: use raw device fingerprint
            identifiedId = deviceFingerprint
        }
    }

    // Build headers
    const headers = {}

    // Add x-identified-id header
    if (identifiedId) {
        headers['x-identified-id'] = identifiedId
    }

    // Add x-store-slug header if storeId exists
    if (storeSlug) {
        headers['x-store-slug'] = storeSlug
    }

    // Add x-owner-email header if email exists
    if (ownerEmail) {
        headers['x-owner-email'] = ownerEmail
    }

    return headers
}

/**
 * Check remaining image generation tokens/credits
 * @returns {Promise<Object>} - Response containing remaining tokens/credits info
 */
export const checkRemainingImageTokens = async () => {
    try {
        const headers = await buildGenaiHeaders()

        const response = await api.makePublicRequest({
            url: '/genai/image-token',
            method: 'GET',
            headers,
        })

        // Response structure: { data: { success: true, data: { token: {...} } } }
        const tokenData = response?.data?.data?.token || response?.data?.token || response?.data || response

        // Return normalized token info
        return {
            remaining: tokenData.available || 0,
            remainingTokens: tokenData.available || 0,
            used: tokenData.used || 0,
            total: tokenData.available + (tokenData.used || 0) || 0,
            totalTokens: tokenData.available + (tokenData.used || 0) || 0,
            nextCycleDate: tokenData.next_cycle_date || null,
            token: tokenData, // Keep full token object for reference
        }
    } catch (error) {
        console.error('[GenerateBackground] Failed to check remaining tokens:', error)
        throw error
    }
}

export const generateBackgroundImages = async ({
    imageUrl = null,
    promptText,
    count = '2',
    size = '1K',
    ratio = '1:1',
}) => {
    const payload = {
        text: promptText,
        count: count,
        size: size,
        ratio: ratio,
    }

    if (imageUrl) {
        payload.imageUrl = imageUrl
    }

    // Build headers using reusable function
    const headers = await buildGenaiHeaders()
    headers['Content-Type'] = 'application/json'

    return api.makePublicRequest({
        url: '/genai/images',
        method: 'POST',
        data: payload,
        headers,
    })
}

const GenerateBackgroundServices = {
    generateBackgroundImages,
    loadGenerateBackgroundConfig,
    convertResolutionToSizeAndRatio,
    checkRemainingImageTokens,
    buildGenaiHeaders,
}

export default GenerateBackgroundServices

